import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { STAY_PLAN_TABLES, type StayPlanModule } from "@/lib/daycare-hotel/stayPlans";

export type StayPlanAdjustmentReason =
  | "courtesy"
  | "miscount"
  | "missed_day"
  | "commercial"
  | "extra_day"
  | "other";

export const STAY_PLAN_ADJUSTMENT_REASONS: StayPlanAdjustmentReason[] = [
  "courtesy",
  "miscount",
  "missed_day",
  "commercial",
  "extra_day",
  "other",
];

export interface StayPlanAdjustment {
  id: string;
  contractId: string;
  delta: number;
  /** `null` em pacote de diárias, que não tem ciclo de cobrança. */
  cycleStartsOn: string | null;
  cycleEndsOn: string | null;
  /** Segunda-feira da semana que um ajuste semanal afeta. */
  targetWeekStartsOn: string | null;
  /** Diária cancelada para abrir a semana-alvo, quando foi necessário. */
  cancelledStayReservationDayId: string | null;
  reason: StayPlanAdjustmentReason;
  note: string | null;
  chargeAmount: number | null;
  chargeSaleItemId: string | null;
  reversesAdjustmentId: string | null;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AdjustmentRow = any;

export function normalizeStayPlanAdjustment(row: AdjustmentRow): StayPlanAdjustment {
  return {
    id: row.id,
    contractId: row.contract_id,
    delta: Number(row.delta),
    cycleStartsOn: row.cycle_starts_on ?? null,
    cycleEndsOn: row.cycle_ends_on ?? null,
    targetWeekStartsOn: row.target_week_starts_on ?? null,
    cancelledStayReservationDayId: row.cancelled_stay_reservation_day_id ?? null,
    reason: row.reason as StayPlanAdjustmentReason,
    note: row.note ?? null,
    chargeAmount: row.charge_amount === null || row.charge_amount === undefined ? null : Number(row.charge_amount),
    chargeSaleItemId: row.charge_sale_item_id ?? null,
    reversesAdjustmentId: row.reverses_adjustment_id ?? null,
    createdBy: row.created_by,
    createdByName: row.author?.full_name ?? null,
    createdAt: row.created_at,
  };
}

export const stayPlanAdjustmentsKey = (module: StayPlanModule, contractId: string | null): unknown[] => [
  "stay-plan-adjustments",
  module,
  contractId,
];

const ADJUSTMENT_COLUMNS =
  "id, contract_id, delta, cycle_starts_on, cycle_ends_on, target_week_starts_on, cancelled_stay_reservation_day_id, reason, note, " +
  "charge_amount, charge_sale_item_id, reverses_adjustment_id, created_by, created_at";

/**
 * O embed do autor vai pelo NOME da constraint, não pelo nome da coluna: é a
 * convenção do resto do schema e não fica ambíguo se um dia aparecer outra FK
 * para profiles nesta tabela.
 */
export function stayPlanAdjustmentSelect(module: StayPlanModule): string {
  const table = STAY_PLAN_TABLES[module].adjustments;
  return `${ADJUSTMENT_COLUMNS}, author:profiles!${table}_created_by_fkey(full_name)`;
}

export function useStayPlanAdjustments(
  module: StayPlanModule,
  contractId: string | null,
): UseQueryResult<StayPlanAdjustment[]> {
  return useQuery<StayPlanAdjustment[]>({
    queryKey: stayPlanAdjustmentsKey(module, contractId),
    enabled: Boolean(contractId),
    queryFn: async () => {
      if (!contractId) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from(STAY_PLAN_TABLES[module].adjustments)
        .select(stayPlanAdjustmentSelect(module))
        .eq("contract_id", contractId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return ((data ?? []) as AdjustmentRow[]).map(normalizeStayPlanAdjustment);
    },
  });
}

export interface ApplyStayPlanAdjustmentInput {
  module: StayPlanModule;
  contractId: string;
  delta: number;
  reason: StayPlanAdjustmentReason;
  note?: string | null;
  chargeAmount?: number | null;
  reversesId?: string | null;
  removeCharge?: boolean;
}

export interface WeeklyPlanRemovalCandidate {
  stayReservationDayId: string;
  stayReservationId: string;
  stayDate: string;
  period: string | null;
  isOvernight: boolean;
}

export type WeeklyPlanRemovalPreview =
  | { kind: "target"; targetWeekStartsOn: string }
  | { kind: "needs_cancellation"; candidates: WeeklyPlanRemovalCandidate[] };

export const weeklyPlanRemovalPreviewKey = (module: StayPlanModule, contractId: string | null, referenceDate: string) => [
  "weekly-plan-removal-preview", module, contractId, referenceDate,
];

function normalizeWeeklyPlanRemovalPreview(value: unknown): WeeklyPlanRemovalPreview {
  const row = value as Record<string, unknown> | null;
  if (row?.kind === "target" && typeof row.targetWeekStartsOn === "string") {
    return { kind: "target", targetWeekStartsOn: row.targetWeekStartsOn };
  }
  if (row?.kind === "needs_cancellation" && Array.isArray(row.candidates)) {
    const candidates = row.candidates.map((candidate) => candidate as Record<string, unknown>).filter((candidate) =>
      typeof candidate.stayReservationDayId === "string"
      && typeof candidate.stayReservationId === "string"
      && typeof candidate.stayDate === "string",
    ).map((candidate) => ({
      stayReservationDayId: candidate.stayReservationDayId as string,
      stayReservationId: candidate.stayReservationId as string,
      stayDate: candidate.stayDate as string,
      period: typeof candidate.period === "string" ? candidate.period : null,
      isOvernight: Boolean(candidate.isOvernight),
    }));
    return { kind: "needs_cancellation", candidates };
  }
  throw new Error("invalid_weekly_removal_preview");
}

export function useWeeklyPlanRemovalPreview({
  module,
  contractId,
  referenceDate,
  enabled,
}: {
  module: StayPlanModule;
  contractId: string | null;
  referenceDate: string;
  enabled: boolean;
}): UseQueryResult<WeeklyPlanRemovalPreview> {
  return useQuery({
    queryKey: weeklyPlanRemovalPreviewKey(module, contractId, referenceDate),
    enabled: enabled && Boolean(contractId),
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("preview_weekly_plan_removal", {
        p_module: module,
        p_contract_id: contractId,
        p_reference_date: referenceDate,
      });
      if (error) throw error;
      return normalizeWeeklyPlanRemovalPreview(data);
    },
  });
}

export interface ApplyWeeklyPlanRemovalInput {
  module: StayPlanModule;
  contractId: string;
  reason: StayPlanAdjustmentReason;
  note?: string | null;
  cancelStayReservationDayId?: string | null;
}

export interface ReverseWeeklyPlanRemovalInput {
  module: StayPlanModule;
  contractId: string;
  reversesAdjustmentId: string;
  reason: StayPlanAdjustmentReason;
  note?: string | null;
}

const ERROR_KEYS = new Set([
  "contract_not_found",
  "permission_denied",
  "invalid_delta",
  "note_required",
  "insufficient_balance",
  "already_reversed",
  "invalid_remove_charge",
  "owner_required",
  "weekly_removal_not_supported",
  "weekly_removal_cancel_choice_required",
  "weekly_removal_invalid_day",
  "weekly_removal_preview_stale",
]);

function invalidateWeeklyRemovalQueries(queryClient: ReturnType<typeof useQueryClient>, input: { module: StayPlanModule; contractId: string }) {
  queryClient.invalidateQueries({ queryKey: stayPlanAdjustmentsKey(input.module, input.contractId) });
  queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
  queryClient.invalidateQueries({ queryKey: ["stay-plan-rows"] });
  queryClient.invalidateQueries({ queryKey: ["panel-day"] });
  queryClient.invalidateQueries({ queryKey: ["stay-schedule"] });
  queryClient.invalidateQueries({ queryKey: ["patient-history-feed"] });
  queryClient.invalidateQueries({ queryKey: ["owner-history-feed"] });
}

export function useApplyStayPlanAdjustment(): UseMutationResult<string, unknown, ApplyStayPlanAdjustmentInput> {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async (input: ApplyStayPlanAdjustmentInput) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("apply_stay_plan_balance_adjustment", {
        p_module: input.module,
        p_contract_id: input.contractId,
        p_delta: input.delta,
        p_reason: input.reason,
        p_note: input.note ?? null,
        p_charge_amount: input.chargeAmount ?? null,
        p_reverses_id: input.reversesId ?? null,
        p_remove_charge: input.removeCharge ?? false,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_id, input) => {
      queryClient.invalidateQueries({ queryKey: stayPlanAdjustmentsKey(input.module, input.contractId) });
      queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["stay-plan-rows"] });
      toast({ title: t("stayPlanAdjustments.saved") });
    },
    onError: (error: unknown) => {
      const message = (error as { message?: string } | null)?.message ?? "";
      const code = ERROR_KEYS.has(message) ? message : "unknown";
      toast({ variant: "destructive", title: t(`stayPlanAdjustments.errors.${code}`) });
    },
  });
}

export function useApplyWeeklyPlanRemoval(): UseMutationResult<string, unknown, ApplyWeeklyPlanRemovalInput> {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async (input) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("apply_weekly_plan_removal", {
        p_module: input.module,
        p_contract_id: input.contractId,
        p_reason: input.reason,
        p_note: input.note ?? null,
        p_cancel_stay_reservation_day_id: input.cancelStayReservationDayId ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_id, input) => {
      invalidateWeeklyRemovalQueries(queryClient, input);
      toast({ title: t("stayPlanAdjustments.saved") });
    },
    onError: (error: unknown) => {
      const message = (error as { message?: string } | null)?.message ?? "";
      const code = ERROR_KEYS.has(message) ? message : "unknown";
      toast({ variant: "destructive", title: t(`stayPlanAdjustments.errors.${code}`) });
    },
  });
}

export function useReverseWeeklyPlanRemoval(): UseMutationResult<string, unknown, ReverseWeeklyPlanRemovalInput> {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async (input) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("reverse_weekly_plan_removal", {
        p_module: input.module,
        p_contract_id: input.contractId,
        p_reverses_adjustment_id: input.reversesAdjustmentId,
        p_reason: input.reason,
        p_note: input.note ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_id, input) => {
      invalidateWeeklyRemovalQueries(queryClient, input);
      toast({ title: t("stayPlanAdjustments.saved") });
    },
    onError: (error: unknown) => {
      const message = (error as { message?: string } | null)?.message ?? "";
      const code = ERROR_KEYS.has(message) ? message : "unknown";
      toast({ variant: "destructive", title: t(`stayPlanAdjustments.errors.${code}`) });
    },
  });
}
